import { CircleArrowLeft02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { notify } from '@pique/component';
import {
  getCurrentStore,
  transcriptionCollapsedAtom,
} from '@toeverything/infra';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import type { CallBackProps } from 'react-joyride';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import { authAtom } from '../../atoms';
import { AIProvider } from '../../blocksuite/presets/ai/provider';
import { resetAllFeatureDiscoveryAtom } from '../feature-discovery/atoms';
import {
  appSidebarOpenAtom,
  appSidebarWidthAtom,
  isMobileView,
} from '../../components/app-sidebar/index.jotai';
import { posthog } from '../../posthog';
import {
  MAIN_TOUR_TOTAL_STEPS,
  onboardingStepIndexAtom,
  postAugmentationStepIndexAtom,
} from './atoms';
import { STORAGE_KEYS } from './types';

const NOTIFICATION_DURATION_MS = 5000;

function highlightTourButton(
  duration: number = NOTIFICATION_DURATION_MS
): void {
  const button = document.querySelector(
    '[data-testid="slider-bar-tour-button"]'
  ) as HTMLElement;
  if (!button) return;

  const originalBg = button.style.backgroundColor;
  button.style.backgroundColor = 'rgba(140, 92, 245, 0.12)';
  button.style.borderRadius = '8px';

  setTimeout(() => {
    button.style.backgroundColor = originalBg;
  }, duration);
}

export const useOnboarding = () => {
  const setAuthModal = useSetAtom(authAtom);
  const [persistedStepIndex, setPersistedStepIndex] = useAtom(
    onboardingStepIndexAtom
  );
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showPermissionsDock, setShowPermissionsDock] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyInstanceId, setSurveyInstanceId] = useState(0);
  const surveyTrackedRef = useRef(false);
  const platform = environment.isTauri ? 'desktop' : 'web';
  const resetFeatureDiscovery = useSetAtom(resetAllFeatureDiscoveryAtom);

  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;

  const resetOnboardingState = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.SURVEY_SHOWN);
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_SHOWN);
    localStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_SHOWN);
    localStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_PENDING);
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_STEP_INDEX);
    localStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_STEP_INDEX);
    sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
    sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);
    setIsRunning(false);
    setStepIndex(0);
    setPersistedStepIndex(0);
    setShowPermissionsDock(false);
    setShowSurvey(false);
    setSurveyInstanceId(prev => prev + 1);
    surveyTrackedRef.current = false;
    const store = getCurrentStore();
    store.set(postAugmentationStepIndexAtom, 0);
    resetFeatureDiscovery();
    window.dispatchEvent(new Event('pique-onboarding-state-changed'));
  }, [setPersistedStepIndex, resetFeatureDiscovery]);

  const startTour = useCallback(() => {
    resetOnboardingState();
    setShowSurvey(true);
    sessionStorage.setItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS, 'survey');
  }, [resetOnboardingState]);

  const replayTour = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_SHOWN);
    localStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_SHOWN);
    localStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_PENDING);
    sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
    sessionStorage.removeItem(STORAGE_KEYS.POST_AUGMENTATION_IN_PROGRESS);
    setPersistedStepIndex(0);
    const store = getCurrentStore();
    store.set(postAugmentationStepIndexAtom, 0);
    sessionStorage.setItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS, 'tour');
    window.dispatchEvent(new Event('pique-onboarding-state-changed'));
    posthog.capture('onboarding_tour_started', {
      total_steps: MAIN_TOUR_TOTAL_STEPS,
      source: 'replay',
      platform,
    });
    setTimeout(() => {
      setIsRunning(true);
      setStepIndex(0);
    }, 300);
  }, [setPersistedStepIndex, platform]);

  const handlePermissionsDockClose = useCallback(() => {
    setShowPermissionsDock(false);
    sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
  }, []);

  const triggerPermissionsDock = useCallback(() => {
    if (!environment.isTauri) return;
    setShowPermissionsDock(true);
  }, []);

  const startOnboardingTour = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS, 'tour');
    const target = document.querySelector(
      '[data-testid="new-page-button-trigger"]'
    );
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    posthog.capture('onboarding_tour_started', {
      total_steps: MAIN_TOUR_TOTAL_STEPS,
      source: 'after_survey',
      platform,
    });
    setTimeout(() => {
      setIsRunning(true);
      setStepIndex(0);
      setPersistedStepIndex(0);
    }, 300);
  }, [setPersistedStepIndex, platform]);

  const resumeOnboardingTour = useCallback(
    (fromStep?: number) => {
      const resumeStep = fromStep ?? persistedStepIndex;
      sessionStorage.setItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS, 'tour');
      posthog.capture('onboarding_tour_resumed', {
        resumed_at_step: resumeStep + 1,
        total_steps: MAIN_TOUR_TOTAL_STEPS,
        platform,
      });
      setTimeout(() => {
        setIsRunning(true);
        setStepIndex(resumeStep);
      }, 300);
    },
    [persistedStepIndex, platform]
  );

  const handleSurveyComplete = useCallback(() => {
    setShowSurvey(false);
    localStorage.setItem(STORAGE_KEYS.SURVEY_SHOWN, 'true');
    const onboardingShown = localStorage.getItem(STORAGE_KEYS.ONBOARDING_SHOWN);
    if (!onboardingShown) {
      startOnboardingTour();
    }
  }, [startOnboardingTour]);

  const handleSurveyClose = useCallback(() => {
    setShowSurvey(false);
    localStorage.setItem(STORAGE_KEYS.SURVEY_SHOWN, 'true');
    const onboardingShown = localStorage.getItem(STORAGE_KEYS.ONBOARDING_SHOWN);
    if (!onboardingShown) {
      startOnboardingTour();
    }
  }, [startOnboardingTour]);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
    sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
  }, []);

  const handleEarlyExit = useCallback(
    (currentStepIndex: number) => {
      posthog.capture('onboarding_tour_exited', {
        exited_at_step: currentStepIndex + 1,
        total_steps: MAIN_TOUR_TOTAL_STEPS,
        platform,
      });
      setPersistedStepIndex(currentStepIndex);
      setIsRunning(false);
      sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);

      const store = getCurrentStore();
      store.set(
        appSidebarOpenAtom,
        true as boolean | ((prev: boolean) => boolean)
      );

      setTimeout(() => {
        const sidebarWidth = store.get(appSidebarWidthAtom);
        notify(
          {
            title: 'We saved your spot!',
            message: 'Resume the tour anytime from the sidebar.',
            icon: createElement(HugeiconsIcon, {
              icon: CircleArrowLeft02Icon,
              size: 28,
              className: 'onboarding-notification-icon',
            }),
            iconColor: 'var(--affine-primary-color)',
            style: 'normal',
            theme: 'info',
            rootAttrs: {
              style: {
                backgroundColor: 'var(--affine-background-primary-color)',
                color: 'var(--affine-text-primary-color)',
                borderColor: 'rgb(140, 92, 245)',
                boxShadow: 'var(--affine-menu-shadow)',
                borderRadius: '20px',
                border: '1px solid rgb(140, 92, 245)',
                padding: '16px 20px',
                fontSize: '15px',
              },
            },
          },
          {
            duration: NOTIFICATION_DURATION_MS,
            position: 'bottom-left',
            style: {
              marginLeft: `${sidebarWidth + 16}px`,
              marginBottom: '16px',
            },
          }
        );
        highlightTourButton(NOTIFICATION_DURATION_MS);
      }, 300);
    },
    [setPersistedStepIndex, platform]
  );

  useEffect(() => {
    if (!isRunning) return;
    const onOverlay = () => handleEarlyExit(stepIndexRef.current);
    document.addEventListener('pique-overlay-created', onOverlay);
    return () =>
      document.removeEventListener('pique-overlay-created', onOverlay);
  }, [isRunning, handleEarlyExit]);

  const isMobileViewValue = useAtomValue(isMobileView);

  useEffect(() => {
    if (isMobileViewValue) return;

    let timeoutId: number | undefined;

    const checkAuth = (userInfo: { id: string } | null) => {
      const surveyShown = localStorage.getItem(STORAGE_KEYS.SURVEY_SHOWN);
      if (surveyShown) return;

      if (!environment.isTauri && !userInfo?.id) {
        setAuthModal({ openModal: true, state: 'signIn' });
        return;
      }

      if (userInfo?.id) {
        if (timeoutId !== undefined) return;

        timeoutId = window.setTimeout(() => {
          setShowSurvey(true);
          sessionStorage.setItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS, 'survey');
          surveyTrackedRef.current = true;
        }, 1000);
      }
    };

    const userInfoResult = AIProvider.userInfo;
    if (userInfoResult instanceof Promise) {
      userInfoResult.then(checkAuth).catch(() => {});
    } else {
      checkAuth(userInfoResult);
    }

    const disposable = AIProvider.slots.userInfo.on(checkAuth);

    return () => {
      disposable.dispose();
      clearTimeout(timeoutId);
    };
  }, [setAuthModal, isMobileViewValue]);

  useEffect(() => {
    if (isMobileViewValue) return;

    const inProgress = sessionStorage.getItem(
      STORAGE_KEYS.ONBOARDING_IN_PROGRESS
    );
    if (
      inProgress === 'tour' &&
      localStorage.getItem(STORAGE_KEYS.ONBOARDING_SHOWN) !== 'true'
    ) {
      resumeOnboardingTour();
    }
  }, [resumeOnboardingTour, isMobileViewValue]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      if (status === STATUS.FINISHED) {
        posthog.capture('onboarding_tour_completed', {
          total_steps: MAIN_TOUR_TOTAL_STEPS,
          platform,
        });
        setPersistedStepIndex(0);
        stopTour();
        localStorage.setItem(STORAGE_KEYS.ONBOARDING_SHOWN, 'true');
        sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
        window.dispatchEvent(new Event('pique-onboarding-state-changed'));
        return;
      }

      if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        handleEarlyExit(index);
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextStepIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
        if (nextStepIndex >= MAIN_TOUR_TOTAL_STEPS) {
          posthog.capture('onboarding_tour_completed', {
            total_steps: MAIN_TOUR_TOTAL_STEPS,
            platform,
          });
          setPersistedStepIndex(0);
          stopTour();
          localStorage.setItem(STORAGE_KEYS.ONBOARDING_SHOWN, 'true');
          sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
          window.dispatchEvent(new Event('pique-onboarding-state-changed'));
          return;
        }
        posthog.capture('onboarding_tour_step_completed', {
          step: index + 1,
          next_step: nextStepIndex + 1,
          total_steps: MAIN_TOUR_TOTAL_STEPS,
          platform,
        });
        if (index === 1 && nextStepIndex === 2) {
          const store = getCurrentStore();
          const isCollapsed = store.get(transcriptionCollapsedAtom);
          const pauseBtn = document.querySelector(
            '[aria-label="Stop Recording"]'
          );
          if (pauseBtn || !isCollapsed) {
            if (pauseBtn) {
              (pauseBtn as HTMLElement).click();
            }
            store.set(transcriptionCollapsedAtom, true);
            setTimeout(() => {
              setStepIndex(nextStepIndex);
              setPersistedStepIndex(nextStepIndex);
            }, 400);
            return;
          }
        }
        setStepIndex(nextStepIndex);
        setPersistedStepIndex(nextStepIndex);
      } else if (type === EVENTS.TARGET_NOT_FOUND) {
        const nextStepIndex = index + 1;
        if (nextStepIndex >= MAIN_TOUR_TOTAL_STEPS) {
          posthog.capture('onboarding_tour_completed', {
            total_steps: MAIN_TOUR_TOTAL_STEPS,
            platform,
          });
          setPersistedStepIndex(0);
          stopTour();
          localStorage.setItem(STORAGE_KEYS.ONBOARDING_SHOWN, 'true');
          sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_IN_PROGRESS);
          window.dispatchEvent(new Event('pique-onboarding-state-changed'));
          return;
        }
        setStepIndex(nextStepIndex);
        setPersistedStepIndex(nextStepIndex);
      }
    },
    [stopTour, handleEarlyExit, setPersistedStepIndex, platform]
  );

  return {
    startTour,
    replayTour,
    stopTour,
    resumeOnboardingTour,
    isRunning,
    stepIndex,
    persistedStepIndex,
    handleJoyrideCallback,
    showPermissionsDock,
    handlePermissionsDockClose,
    triggerPermissionsDock,
    showSurvey,
    surveyInstanceId,
    handleSurveyComplete,
    handleSurveyClose,
  };
};
